import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import ReportData from "go-report/reports";
import {
	getLastMonthFilterBetween,
	getReportCustomFieldsResources,
	reportParamsConverter,
} from "go-report/reports/services/report-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { api } from "../../../../../../../../../../services/Api/api";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "DIAGNOSTIC_OPEN_AMOUNT";
const resourceType = "DIAGNOSTIC_OPEN_AMOUNT";

const INITIAL_SELECTED_COLUMNS = [
	// "open_amount_from_layer",
	// "should_open_amount",
	"income_open_amount_diff",
	"expanse_open_amount_diff",
	"should_price_diff",
	// "actual_amount",
	// "open_amount_price_from_layer",
	// "actual_price",
	// "last_price",
	// "should_price",
	// // "used_layer_amount",
	// // "used_layer_price",
	// // "used_layer_items_amount",
	// // "used_layer_items_price",
	// "expanse_used_amount",
	// "should_price_diff",
	// "should_price_percent",
	// "used_layer_amount_diff",
	// "last_price_diff",
];

const Report = ({ resource }: { resource?: Record<string, any> }) => {
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const organization = useSelector(selectOrganization);

	if (!resource) return null;

	const data = resource.read();

	let config = {
		reportConfigId: "DIAGNOSTIC_OPEN_AMOUNT",
		aggregatePrefix: "summary",
		type: "bill",
		selectedColumns: INITIAL_SELECTED_COLUMNS,
		showPercent: false,
		selectedGroups: ["PRODUCT"],
		groups: [
			{
				id: "PRODUCT",
				name: t("common.word.product"),
			},
			{
				id: "STOCK",
				name: t("common.word.stock"),
			},
			{
				id: "TYPE",
				name: "Typ",
			},
		],
		groupsInfo: {
			PRODUCT: (groupByValue: Record<string, any>) => {
				return (
					<div>
						<Link
							to={`/${organization.id}/products/${groupByValue.id}/edit`}
							rel="noreferrer"
							target="_blank"
						>
							{groupByValue.name}
						</Link>
						{` | `}
						<Link
							to={`/${organization.id}/settings/diagnostic/product/open-amount?product_id=${groupByValue.id}`}
							rel="noreferrer"
							target="_blank"
						>
							DIAGNOSTYKA
						</Link>
					</div>
				);
			},
		},
		columns: [
			{
				id: "open_amount_from_layer",
				name: "Otwarta ilość (+ / Layer)",
			},
			{
				id: "should_open_amount",
				name: "Poprawna otwarta ilość (+)",
			},
			{
				id: "income_open_amount_diff",
				name: "Różnica otwarta ilość (+)",
			},
			{
				id: "actual_amount",
				name: "Aktualna ilość",
			},
			{
				id: "open_amount_price_from_layer",
				name: "Otwarta wartość (Layer)",
			},
			{
				id: "actual_price",
				name: "Aktualna wartość",
			},
			{
				id: "should_price",
				name: "Poprawna wartość (fifo)",
			},
			{
				id: "used_layer_amount",
				name: "Użyta ilość (+)",
			},
			{
				id: "expanse_used_amount",
				name: "Użyta ilość (-)",
			},
			{
				id: "expanse_open_amount",
				name: "Otwarta ilość (-)",
			},
			{
				id: "used_layer_price",
				name: "Użyta wartość",
			},
			{
				id: "used_layer_items_amount",
				name: "Użyta ilość (Layer items)",
			},
			{
				id: "used_layer_items_price",
				name: "Użyta wartość (Layer items)",
			},
			{
				id: "should_price_diff",
				name: "Różnica aktualnej wartości",
			},
			{
				id: "last_price_diff",
				name: "Różnica wartości Ostatnia cena",
			},
			{
				id: "should_price_percent",
				name: "Różnica aktualnej wartości (%)",
				render: (val: number) => {
					return `${val.toFixed(2)} %`;
				},
			},
			{
				id: "expanse_open_amount_diff",
				name: "Róźnica otwarta ilość (-)",
			},
			{
				id: "used_layer_amount_diff",
				name: "Różnica ilości użytych layerów",
			},
			{
				id: "used_layer_price_diff",
				name: "Różnica wartości użytych layerów",
			},
		],
		filters: [
			{
				id: "stock_ids",
				name: t("common.word.stock"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getStockSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "product_ids",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getProductSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			// {
			// 	id: "date_range",
			// 	name: t("common.word.date", { ns: "lib" }),
			// 	type: "date",
			// 	typeOptions: ["bt"],
			// },
			{
				id: "type",
				name: "typ błędu",
				type: "list",
				listOptions: {
					GOOD: "Poprawna ilość i wartość",
					PRICE_PLUS: "Cena przy ilości plus",
					PRICE_MINUS: "Cena przy ilości minus",
					AMOUNT: "Błędna ilość użytych",
					WARNING: "Warning",
				},
			},
			{
				id: "warning",
				name: "Potencjalne błędy",
				type: "boolean",
			},
			{
				id: "open_amount_from_layer",
				name: "Otwarta ilość (Layer)",
				type: "number",
			},
			{
				id: "actual_amount",
				name: "Aktualna ilość",
				type: "number",
			},
			{
				id: "open_amount_price_from_layer",
				name: "Otwarta wartość (Layer)",
				type: "number",
			},
			{
				id: "actual_price",
				name: "Aktualna wartość",
				type: "number",
			},
			{
				id: "should_price",
				name: "Poprawna wartość (fifo)",
				type: "number",
			},
			{
				id: "should_price_diff",
				name: "Różnica aktualnej wartości",
				type: "number",
			},
			{
				id: "used_layer_amount_diff",
				name: "Różnica ilości użytych layerów",
				type: "number",
			},
			{
				id: "used_layer_price_diff",
				name: "Różnica wartości użytych layerów",
				type: "number",
			},
			{
				id: "should_price_percent",
				name: "Różnica aktualnej wartości (%)",
				type: "number",
			},
			{
				id: "date_range",
				name: t("common.word.date", { ns: "lib" }),
				type: "date",
				typeOptions: ["bt"],
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				columns: INITIAL_SELECTED_COLUMNS,
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween({
							getDateFromLocalStorage: true,
							includeBoundaryDays: true,
						}).split("=")[1],
						condition: getLastMonthFilterBetween({
							getDateFromLocalStorage: true,
							includeBoundaryDays: true,
						}).split("=")[0],
					},
				],
				groups: ["PRODUCT"],
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			delete params.chart_type;
			return Promise.all([
				apiOrganization.getOrganizationDiagnosticOpenAmountReports(
					reportParamsConverter(params),
					sourceToken?.token
				),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: "Diagnostyka OPEN AMOUNT",
			filename: "Diagnostyka OPEN AMOUNT",
			organization: organization.name,
		},
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, [], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const OrganizationSettingsDiagnosticOpenAmountPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const [loadingFix, setLoadingFix] = useState<boolean>(false);
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const { addSuccessFlash, addFlash } = useFlash();

	const fixOpenAmount = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message:
					"Czy na pewno chcesz poprawić otwere ilości, operacja jest nieodwracalna. Ustawi on otwarte ilości (dostępne w FIFO) według ilości z aktualnego stanu",
			});
			setLoadingFix(true);
			await apiOrganization.fixDiagnosticOpenAmount();
			addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoadingFix(false);
	};
	useEffect(() => {
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, [])));
	}, []);

	return (
		<>
			<Header title={t("modules.product_report.header.title")} />
			<div>
				<ButtonLoading loading={loadingFix} className={"btn btn-default"} onClick={fixOpenAmount}>
					Popraw otwarte ilości
				</ButtonLoading>
			</div>
			<div>
				<div className="card card-body bg-light mt-2 mb-2">
					<h3>Legenda</h3>
					<dl>
						<h4>Otwarte lości</h4>
						<dt>Różnica otwarta ilość (+)</dt>
						<dd>Pokazuje czy ilość dostępnych zejść jest prawdiłowa z aktualnym stanem</dd>
						<dt>Różnica aktualnej wartość</dt>
						<dd>
							Pokazuje jaka jest różnica pomiędzy aktualna wartością a jaka powinna być wartość według
							otwartch transakcji
						</dd>

						<dt>Różnica otwarta ilość (-)</dt>
						<dd>Pokazuje ile zejść jest w trakcie</dd>

						<dt>Otwarta ilość (+ / Layer)</dt>
						<dd>Pokazuje jaka jest otwara ilosc (dostępne ilosci do zejścia)</dd>
						<dt>Otwarta ilość (-)</dt>
						<dd>Pokazuje ile zejść nie ma przypiętych przychodów (np. zakupów)</dd>
						<dt>Poprawna otwarta ilość</dt>
						<dd>Pokazuje jaka powinna być ilość dostępnych zejść na podstawie aktualnego stanu</dd>
					</dl>
				</div>
			</div>
			<Suspense fallback={<LoadingContainer />}>
				<Report resource={resource} />
			</Suspense>
		</>
	);
};

export default OrganizationSettingsDiagnosticOpenAmountPage;
